import {gql} from "@apollo/client";

import {ContactGroupItems} from "@/lib/queries/contacts.ts";
import {GraphQLResponse} from "@/lib/types/common.ts";
import {IntegrationListData} from "@/lib/types/integrations.ts";

export const GQL_INTEGRATIONS_QUERY = gql`
  query Integrations($sort: String) {
    integrations(sort: $sort) {
      items {
        id
        module
        name
        contact_groups {
          id
        }
        is_enabled
        is_errored
        settings
        errors {
          id
          event
          created_at
          short_error
        }
      }
      total
    }
  }
`;

export const GQL_INTEGRATION_ERROR_QUERY = gql`
  query IntegrationError($id: Int!) {
    integrationError(id: $id) {
      id
      full_error
    }
  }
`;

export interface IntegrationErrorQueryResponse {
  integrationError: {id: number; full_error: string} | null;
}

export const GQL_INTEGRATIONS_FORM_DEPENDENCY_QUERY = gql`
  query IntegrationDependencies {
    availableContactGroups: contactGroups(limit: 100, offset: 0) {
      total
      items {
        id
        name
      }
    }
  }
`;

export interface IntegrationFormDependencyResult {
  availableContactGroups: ContactGroupItems;
}

export interface IntegrationsItems {
  items: IntegrationListData[];
  total: number;
}

export interface IntegrationsQueryResponse {
  integrations: IntegrationsItems;
}

export const GQL_INTEGRATION_SAVE = gql`
  mutation SaveIntegration($data: SaveIntegrationInput!) {
    response: saveIntegration(data: $data) {
      ... on SaveIntegrationSuccessResponse {
        status
        data {
          id
          name
          module
          contact_groups {
            id
          }
          is_enabled
          is_errored
          settings
        }
      }
      ... on ErrorResponse {
        status
        message
      }
    }
  }
`;

export const GQL_INTEGRATION_DELETE = gql`
  mutation DeleteIntegration($id: Int!) {
    response: deleteIntegration(id: $id) {
      ... on BaseResponse {
        status
      }
      ... on ErrorResponse {
        status
        message
      }
    }
  }
`;

export interface TelegramChat {
  chat_id: string;
  chat_title: string;
}

export interface TelegramChatsData {
  items: TelegramChat[];
  total: number;
}

export interface TelegramChatsQueryResponse {
  telegramChats: TelegramChatsData;
}

export const GQL_TELEGRAM_CHATS = gql`
  query TelegramChats($username: String!) {
    telegramChats(username: $username) {
      items {
        chat_id
        chat_title
      }
    }
  }
`;

export const GQL_SEND_MAKE_WEBHOOK_TEST = gql`
  mutation SendMakeWebhookTest($webhookUrl: String!) {
    response: sendMakeWebhookTest(webhookUrl: $webhookUrl) {
      ... on BaseResponse {
        status
      }
      ... on ErrorResponse {
        status
        message
      }
    }
  }
`;

export type SaveIntegrationResult = GraphQLResponse<Omit<IntegrationListData, "errors">>;

export interface PopularIntegration {
  name: string;
  module: string;
  count: number;
}

export interface PopularIntegrationsData {
  items: PopularIntegration[];
  total: number;
}

export interface PopularIntegrationsQueryResponse {
  popularIntegrations: PopularIntegrationsData;
}

export const GQL_INTEGRATIONS_PAGE_QUERY = gql`
  query IntegrationsPage($sort: String, $limit: Int = 6) {
    integrations(sort: $sort) {
      items {
        id
        module
        name
        contact_groups {
          id
        }
        is_enabled
        is_errored
        settings
        errors {
          id
          event
          created_at
          short_error
        }
      }
      total
    }
    popularIntegrations(limit: $limit) {
      items {
        module
        name
        count
      }
      total
    }
  }
`;

export interface IntegrationsPageQueryResponse {
  integrations: IntegrationsItems;
  popularIntegrations: PopularIntegrationsData;
}
